import styled, { css } from 'styled-components';
import { HiOutlineDocument } from 'react-icons/hi2';
import { abstractBackgroundMinimalTech, panelSurface, messageSurface } from './BackgroundPatterns';
import { techColors, neonGlow, alpha, terminalBorder, terminalTextStyle, terminalHeaderStyle, dangerButtonDefaultGlow, dangerButtonHoverStyles, positiveButtonDefaultGlow, positiveButtonHoverStyles } from './TechRetroEffects';

const barIconButtonStyles = css`
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: 4px;
  border: 1px solid ${techColors.borderSubtle};
  background: ${techColors.darkBgTertiary};
  color: ${techColors.neonPurple};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.2s ease, box-shadow 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    background: ${techColors.darkBg};
    ${neonGlow(techColors.neonCyan, 'normal')}
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

export const StyledMessageContent = styled.div`
  width: 100%;
  min-width: 0;
  padding-bottom: 8px;
  div {
    width: 100%;
    min-width: 0;
    word-wrap: break-word;
    overflow-wrap: break-word;
    word-break: break-word;
    white-space: pre-wrap;
    line-height: 1.45;
    overflow-x: hidden;
  }
`;

export const MessageTimestamp = styled.small`
  position: absolute;
  bottom: 4px;
  right: 8px;
  font-size: 10px;
  opacity: 0.65;
  font-weight: 500;
  white-space: nowrap;
  pointer-events: none;
  z-index: 1;
  max-width: calc(100% - 16px);
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ConnectionIndicator = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isConnected' && prop !== 'isUnknown',
})`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${({ isConnected, isUnknown }) => {
    if (isUnknown) return techColors.statusHidden;
    return isConnected ? techColors.online : techColors.offlineStatus;
  }};
  flex-shrink: 0;
`;

export const WorkspaceContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  height: 100dvh;
  width: 100vw;
  ${abstractBackgroundMinimalTech}
  color: ${techColors.textPrimary};
  position: relative;
  overflow: hidden;
  padding: 6px;
  padding-top: max(6px, env(safe-area-inset-top));
  padding-right: max(6px, env(safe-area-inset-right));
  padding-bottom: max(6px, env(safe-area-inset-bottom));
  padding-left: max(6px, env(safe-area-inset-left));
  box-sizing: border-box;
  ${terminalTextStyle}

  @media (max-width: 768px) {
    padding: 0;
    padding-top: env(safe-area-inset-top);
    padding-right: env(safe-area-inset-right);
    padding-bottom: env(safe-area-inset-bottom);
    padding-left: env(safe-area-inset-left);
  }
`;

export const Messages = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$isGroupChat',
})`
  flex: 1;
  padding: ${({ $isGroupChat }) => ($isGroupChat ? '16px 24px 16px 16px' : '16px 32px')};
  overflow-y: auto;
  overflow-anchor: none;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: auto;
  ${messageSurface}

  @media (max-width: 768px) {
    padding: ${({ $isGroupChat }) => ($isGroupChat ? '12px 14px 12px 12px' : '12px 14px')};
  }
`;

export const MessageWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser' && prop !== 'isGrouped' && prop !== '$isFirstAfterDate' && prop !== '$isGroupChat',
})`
  margin-top: ${({ isGrouped, $isFirstAfterDate }) => {
    if (isGrouped) return '2px';
    if ($isFirstAfterDate) return '0';
    return '8px';
  }};
  margin-bottom: 0;
  margin-left: ${({ isUser, $isGroupChat }) => ($isGroupChat || isUser ? '0' : '12px')};
  margin-right: ${({ isUser, $isGroupChat }) => ($isGroupChat || !isUser ? '0' : '12px')};
  display: flex;
  align-items: flex-end;
  gap: 8px;
  justify-content: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  flex-direction: row;
`;

export const Message = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser',
})`
  margin-bottom: 0;
  display: flex;
  flex-direction: column;
  align-items: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  justify-content: flex-end;
  flex: 1;
  min-width: 0;
`;

export const GroupSenderLabel = styled.div`
  font-size: 11px;
  color: ${techColors.textSecondary};
  margin-bottom: 4px;
  padding-left: 2px;
  max-width: min(60%, 500px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${terminalTextStyle}
`;

export const Avatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'color',
})`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background-color: ${({ color }) => color || techColors.textSecondary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
  font-weight: 600;
  flex-shrink: 0;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  overflow: hidden;

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
    font-size: 15px;
  }
`;

export const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

export const AvatarSpacer = styled.div`
  width: 40px;
  height: 40px;
  flex-shrink: 0;

  @media (max-width: 480px) {
    width: 44px;
    height: 44px;
  }
`;

export const ChatHeaderName = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: ${techColors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  ${terminalHeaderStyle}
  text-transform: none;
  letter-spacing: 0.02em;
`;

export const ChatTopBarPlaceholder = styled.span`
  flex: 1;
  text-align: center;
  font-size: 12px;
  color: ${techColors.textSecondary};
  ${terminalTextStyle}
`;

export const ChatHeaderStatus = styled.span`
  font-size: 11px;
  color: ${({ $tone }) => {
    if ($tone === 'online') return techColors.neonCyan;
    if ($tone === 'offline') return techColors.offline;
    return techColors.textSecondary;
  }};
  white-space: nowrap;
  ${terminalTextStyle}
`;

export const EmptyChatState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 24px;
  text-align: center;
  color: ${techColors.textSecondary};
  ${terminalTextStyle}

  strong {
    color: ${techColors.neonCyan};
    font-weight: 600;
  }
`;

export const ChatListItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  gap: 10px;
`;

export const ChatListItemAvatarWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ChatListItemAvatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'color' && prop !== '$favorites',
})`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background-color: ${({ color, $favorites }) => ($favorites ? techColors.darkBgTertiary : (color || techColors.textSecondary))};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 600;
  flex-shrink: 0;
  overflow: hidden;
  ${({ $favorites }) => $favorites && `border: 1px solid ${techColors.borderSubtle};`}
  ${terminalTextStyle}
`;

export const ChatListItemAvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 50%;
`;

export const ChatListItemAvatarStatus = styled.div`
  position: absolute;
  bottom: -1px;
  right: -1px;
  width: 11px;
  height: 11px;
  border-radius: 50%;
  background: ${techColors.darkBg};
  display: flex;
  align-items: center;
  justify-content: center;
`;

export const ChatListItemName = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$unread',
})`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 13px;
  font-weight: ${({ $unread }) => ($unread ? 600 : 500)};
  color: ${techColors.textPrimary};
  ${terminalTextStyle}
`;

export const ChatListItemMain = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
  flex: 1;
`;

export const ChatListItemTop = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5em;
  min-width: 0;
  width: 100%;
  justify-content: space-between;
`;

export const ChatListItemMeta = styled.div`
  font-size: 11px;
  color: ${techColors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  ${terminalTextStyle}
`;

export const ChatListItemPreview = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$unread',
})`
  font-size: 11px;
  color: ${({ $unread }) => ($unread ? techColors.textPrimary : techColors.textSecondary)};
  font-weight: ${({ $unread }) => ($unread ? 500 : 400)};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.3;
  ${terminalTextStyle}
`;

export const ChatListItemTime = styled.span.withConfig({
  shouldForwardProp: (prop) => prop !== '$unread',
})`
  font-size: 10px;
  color: ${({ $unread }) => ($unread ? techColors.textPrimary : techColors.textSecondary)};
  white-space: nowrap;
  flex-shrink: 0;
  ${terminalTextStyle}
`;

export const ChatUnreadDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${techColors.textPrimary};
  box-shadow: 0 0 8px rgba(255, 255, 255, 0.35);
  flex-shrink: 0;
`;

export const CallTimelineRow = styled.div`
  display: flex;
  justify-content: center;
  margin: 10px 0;
  padding: 0 12px;
`;

export const CallTimelineBubble = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$missed',
})`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  max-width: min(100%, 360px);
  padding: 5px 12px;
  border-radius: 14px;
  background: ${techColors.darkBgTertiary};
  border: 1px solid ${({ $missed }) => ($missed ? 'rgba(255, 77, 77, 0.35)' : techColors.borderSubtle)};
  color: ${({ $missed }) => ($missed ? techColors.danger : techColors.textSecondary)};
  font-size: 11px;
  line-height: 1.3;
  ${terminalTextStyle}

  svg {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
  }
`;

export const CallTimelineLabel = styled.span`
  flex: 1;
  min-width: 0;
  text-align: center;
`;

export const CallTimelineTime = styled.span`
  flex-shrink: 0;
  opacity: 0.75;
  font-size: 10px;
`;

export const DateDivider = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 12px 0;
  position: relative;

  &::before,
  &::after {
    content: '';
    flex: 1;
    height: 1px;
    background: linear-gradient(to right, transparent, ${techColors.borderSubtle}, transparent);
  }

  &::before {
    margin-right: 8px;
  }

  &::after {
    margin-left: 8px;
  }

  font-size: 10px;
  color: ${techColors.textSecondary};
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  ${terminalTextStyle}
`;


export const FileName = styled.span`
  font-size: 0.9em;
  color: ${techColors.textPrimary};
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  position: relative;
  z-index: 2;
  ${terminalTextStyle}
`;

export const RemoveFileButton = styled.button`
  background: none;
  border: none;
  color: ${techColors.textSecondary};
  font-size: 1.2em;
  cursor: pointer;
  padding: 4px 8px;
  flex-shrink: 0;
  transition: color 0.2s ease, transform 0.2s ease;
  position: relative;
  z-index: 2;
  ${terminalTextStyle}
  
  &:hover {
    color: ${techColors.danger};
    transform: scale(1.05);
  }
  
  &:active {
    transform: scale(0.95);
  }
`;

export const FileInput = styled.input`
  display: none;
`;

export const ChatContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$mobileHidden',
})`
  position: relative;
  width: 100%;
  height: 100%;
  ${panelSurface}
  display: flex;
  flex-direction: column;
  overflow: hidden;
  flex: 1;
  min-width: 0;
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 4px;
  margin-left: 0;
  margin-right: 0;

  @media (max-width: 768px) {
    height: 100%;
    margin-left: 0;
    border-radius: 0;
    border-left: none;
    border-right: none;
    ${({ $mobileHidden }) => $mobileHidden && css`
      display: none;
    `}
  }
`;

export const ChatTopBar = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
  min-height: 52px;
  padding: 6px 10px;
  padding-right: 80px;
  border-bottom: 1px solid ${techColors.borderSubtle};
  background: ${techColors.darkBgSecondary};
  box-sizing: border-box;
  position: relative;
  overflow: visible;

  @media (max-width: 768px) {
    min-height: 56px;
    padding: 8px 10px;
    padding-right: 64px;
    gap: 6px;
  }
`;

export const ChatPane = styled.div`
  width: 100%;
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
`;

export const VoiceRoomBannerWrap = styled.div`
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  padding: 10px 16px 6px;
`;

export const VoiceRoomBanner = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 12px;
  width: fit-content;
  max-width: 100%;
  padding: 7px 8px 7px 14px;
  border-radius: 999px;
  border: 1px solid ${techColors.neonCyan};
  background: ${alpha(techColors.neonCyan, '22')};
  color: ${techColors.textPrimary};
  ${neonGlow(techColors.neonCyan, 'subtle')}
`;

export const VoiceRoomLiveDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background: ${techColors.neonGreen};
  box-shadow: 0 0 8px ${techColors.neonGreen};
`;

export const VoiceRoomBannerLabel = styled.span`
  font-size: 0.82em;
  letter-spacing: 0.06em;
  color: ${techColors.neonCyan};
  white-space: nowrap;
`;

export const VoiceRoomJoinButton = styled.button`
  border: none;
  background: ${techColors.neonCyan};
  color: ${techColors.darkBg};
  border-radius: 999px;
  padding: 6px 14px;
  cursor: pointer;
  font-size: 0.8em;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: lowercase;
  flex-shrink: 0;

  &:hover {
    ${neonGlow(techColors.neonCyan, 'normal')}
  }
`;

export const MessageBubble = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isUser' && prop !== '$voice',
})`
  max-width: min(60%, 500px);
  min-width: ${({ $voice }) => ($voice ? '220px' : '80px')};
  background: ${({ isUser }) => (isUser ? techColors.darkBgTertiary : techColors.darkBgSecondary)};
  color: ${techColors.textPrimary};
  border: none;
  ${({ isUser }) => (isUser
    ? css`border-right: 2px solid ${techColors.borderSubtle};`
    : css`border-left: 2px solid ${techColors.borderSubtle};`)}
  padding: 8px 12px 18px;
  border-radius: ${({ isUser }) => (isUser ? '8px 8px 2px 8px' : '8px 8px 8px 2px')};
  text-align: left;
  align-self: ${({ isUser }) => (isUser ? 'flex-end' : 'flex-start')};
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.45;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow-wrap: break-word;
  word-break: break-word;
  overflow-x: hidden;
  overflow-y: visible;
  animation: fadeIn 0.18s ease-out;
  position: relative;
  box-sizing: border-box;
  transition: background-color 0.2s ease;

  &:hover {
    background: ${({ isUser }) => (isUser ? techColors.darkBgTertiary : techColors.darkBgSecondary)};
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }

  @media (max-width: 768px) {
    max-width: min(78%, 450px);
    font-size: 13px;
    padding: 9px 12px;
    padding-bottom: 8px;
  }

  @media (max-width: 480px) {
    max-width: min(88%, 100%);
    min-width: ${({ $voice }) => ($voice ? '180px' : '0')};
    font-size: 12px;
    padding: 7px 10px;
    padding-bottom: 8px;
    border-radius: 4px;
  }
`;


export const ChatInput = styled.textarea`
  flex: 1;
  min-height: 40px;
  height: 40px;
  padding: 9px 12px;
  line-height: 1.2;
  border-radius: 4px;
  border: 1px solid ${techColors.borderSubtle};
  margin: 0;
  font-size: 12px;
  color: ${techColors.textPrimary};
  white-space: pre-wrap;
  overflow-wrap: break-word;
  overflow-y: hidden;
  overflow-x: hidden;
  resize: none;
  background: ${techColors.darkBg};
  transition: border-color 0.2s ease;
  box-sizing: border-box;
  ${terminalTextStyle}

  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.28);
  }

  &::placeholder {
    color: ${techColors.textSecondary};
    opacity: 0.6;
  }
`;

export const ChatInputContainer = styled.div`
  display: flex;
  align-items: flex-end;
  padding: 6px 10px;
  padding-bottom: max(6px, env(safe-area-inset-bottom));
  min-height: 52px;
  box-sizing: border-box;
  flex-shrink: 0;
  background: ${techColors.darkBgSecondary};
  border-top: 2px solid ${techColors.borderSubtle};
  justify-content: space-between;
  gap: 8px;

  @media (max-width: 768px) {
    min-height: 56px;
    padding: 8px 10px;
    padding-bottom: max(8px, env(safe-area-inset-bottom));
  }
`;

export const LeftButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

export const InputAndButtonsContainer = styled.div`
  display: flex;
  align-items: flex-end;
  flex: 1;
  gap: 8px;
  justify-content: flex-end;
  min-width: 0;
`;

export const FilePreviewContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress',
})`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: ${techColors.darkBgSecondary};
  border-radius: 4px;
  ${terminalBorder(techColors.neonCyan)}
  position: relative;
  overflow: hidden;
  flex-shrink: 0;
  min-width: fit-content;
  ${terminalTextStyle}

  @media (max-width: 480px) {
    padding: 6px 10px;
    gap: 8px;
  }
`;

export const FileInfoRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const FileIconContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 2.5em;
  height: 2.5em;
  flex-shrink: 0;
  font-size: 1em;
  z-index: 2;
`;

export const FileIcon = styled(HiOutlineDocument)`
  font-size: 1.8em;
  color: ${techColors.neonCyan};
  z-index: 2;
  position: relative;
  width: 100%;
  height: 100%;
`;

export const ProgressOverlay = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'progress',
})`
  position: absolute;
  top: 0;
  left: 0;
  width: ${({ progress }) => progress}%;
  height: 100%;
  background-color: rgba(16, 185, 129, 0.3);
  transition: width 0.3s ease;
  z-index: 1;
  pointer-events: none;
`;

export const AttachButton = styled.label`
  ${barIconButtonStyles}
`;

export const SendButton = styled.button`
  ${barIconButtonStyles}

  &:active {
    transform: scale(0.98);
  }
`;

export const MicButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$active' && prop !== '$level',
})`
  ${barIconButtonStyles}
  position: relative;
  overflow: hidden;
  border-radius: 50%;
  color: ${techColors.textPrimary};
  border-color: ${({ $active, $level }) => {
    if ($active) return `rgba(76, 175, 80, ${0.35 + Math.min(1, $level || 0) * 0.65})`;
    return techColors.borderSubtle;
  }};
  box-shadow: none;

  &:hover:not(:disabled) {
    background: ${techColors.darkBg};
    box-shadow: none;
    color: ${techColors.textPrimary};
  }

  &:disabled {
    opacity: 0.35;
    cursor: default;
  }

  svg {
    position: relative;
    z-index: 1;
  }
`;

export const VoiceCancelButton = styled.button`
  ${barIconButtonStyles}
  border-radius: 50%;
  color: ${techColors.textSecondary};
  box-shadow: none;

  &:hover:not(:disabled) {
    color: ${techColors.danger};
    border-color: ${techColors.danger};
    background: ${techColors.darkBg};
    box-shadow: none;
  }
`;

export const VoiceLevelFill = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$level',
})`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  height: ${({ $level }) => `${Math.round(Math.max(0, Math.min(1, $level || 0)) * 100)}%`};
  background: linear-gradient(
    to top,
    rgba(76, 175, 80, 0.95) 0%,
    rgba(129, 199, 132, 0.75) 100%
  );
  transition: height 0.07s ease-out;
  pointer-events: none;
`;

export const VoiceRecordBar = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
  gap: 10px;
  min-height: 40px;
  padding: 0 4px;
`;

export const VoiceRecordDot = styled.span`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${techColors.danger};
  flex-shrink: 0;
  animation: voicePulse 1s ease-in-out infinite;

  @keyframes voicePulse {
    0%, 100% { opacity: 1; }
    50% { opacity: 0.35; }
  }
`;

export const VoiceRecordTime = styled.span`
  font-size: 12px;
  letter-spacing: 0.06em;
  color: ${techColors.textPrimary};
  min-width: 3.2em;
`;

export const VoicePlayerRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 200px;
  width: 260px;
  max-width: 100%;
  padding: 2px 0 4px;
`;

export const VoicePlayButton = styled.button`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  border: 1px solid ${techColors.borderSubtle};
  background: ${techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  box-shadow: none;

  &:hover {
    background: ${techColors.darkBg};
  }

  svg {
    width: 14px;
    height: 14px;
  }
`;

export const VoiceWaveform = styled.canvas`
  flex: 1;
  min-width: 0;
  height: 36px;
  width: 100%;
  cursor: pointer;
  display: block;
`;

export const VoiceDuration = styled.span`
  font-size: 10px;
  letter-spacing: 0.04em;
  color: ${techColors.textSecondary};
  min-width: 2.6em;
  text-align: right;
  flex-shrink: 0;
`;

export const SidebarContainer = styled.div.withConfig({
  shouldForwardProp: (prop) => !['heightPercent', 'isResizing', '$collapsed', '$fillRemaining'].includes(prop),
})`
  width: 100%;
  height: ${({ heightPercent, $collapsed, $fillRemaining }) => {
    if ($collapsed || $fillRemaining) return 'auto';
    return heightPercent ? `${heightPercent}%` : '50%';
  }};
  flex: ${({ heightPercent, $collapsed, $fillRemaining }) => {
    if ($collapsed) return '0 0 auto';
    if ($fillRemaining) return '1 1 0';
    return heightPercent ? `0 0 ${heightPercent}%` : '0 0 50%';
  }};
  min-height: ${({ $fillRemaining }) => ($fillRemaining ? '0' : 'auto')};
  background: transparent;
  padding: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  transition: ${({ isResizing }) => (isResizing ? 'none' : 'flex 0.2s ease, height 0.2s ease')};

  &:first-child {
    border-bottom: 1px solid ${techColors.borderSubtle};
  }

  &:last-child {
    border-top: none;
  }

  @media (max-width: 768px) {
    padding: 0;
  }
`;

export const VerticalResizer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isResizing',
})`
  width: 100%;
  height: 4px;
  background-color: transparent;
  cursor: row-resize;
  position: relative;
  flex-shrink: 0;
  z-index: 10;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: 0;
    width: 100%;
    height: 8px;
    cursor: row-resize;
  }

  ${({ isResizing }) => isResizing && `
    background-color: rgba(255, 255, 255, 0.2);
  `}

  @media (max-width: 768px) {
    display: none;
  }
`;

export const ChatItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected' && prop !== 'isBlinking' && prop !== 'isUnread',
})`
  padding: 10px 12px;
  margin-bottom: 4px;
  min-height: 56px;
  box-sizing: border-box;
  border-radius: 4px;
  border: 1px solid transparent;
  cursor: pointer;
  position: relative;
  z-index: 1;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  color: ${techColors.textPrimary};
  
  ${({ isSelected, isBlinking, isUnread }) => {
      if (isSelected) {
        return `
        background-color: ${techColors.darkBgTertiary};
        border-color: ${techColors.borderSubtle};
        z-index: 5;
      `;
      }
      if (isBlinking) {
        return `
        background-color: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.45);
        animation: chatBlinkPulse 0.85s ease-in-out 3;

        @keyframes chatBlinkPulse {
          0%, 100% {
            background-color: rgba(255, 255, 255, 0.04);
            border-color: rgba(255, 255, 255, 0.25);
            box-shadow: inset 0 0 0 0 rgba(255, 255, 255, 0);
          }
          50% {
            background-color: rgba(255, 255, 255, 0.16);
            border-color: rgba(255, 255, 255, 0.7);
            box-shadow: inset 0 0 14px rgba(255, 255, 255, 0.12), 0 0 10px rgba(255, 255, 255, 0.08);
          }
        }
      `;
      }
      if (isUnread) {
        return `
        background-color: rgba(255, 255, 255, 0.05);
        border-color: rgba(255, 255, 255, 0.28);
      `;
      }
      return `
      background-color: transparent;
    `;
  }}

  &:hover {
    background-color: ${({ isSelected }) => (isSelected ? techColors.darkBgTertiary : techColors.darkBgMid)};
    border-color: ${techColors.borderMuted};
  }
`;

export const FileItem = styled.div`
  padding: 3px 6px;
  margin-bottom: 1px;
  border-radius: 3px;
  border: 1px solid transparent;
  background-color: transparent;
  transition: background-color 0.15s ease, border-color 0.15s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
  cursor: pointer;
  position: relative;
  min-height: 24px;

  &:hover {
    background-color: ${techColors.darkBgMid};
    border-color: ${techColors.borderMuted};
  }
`;

export const FileItemName = styled.div`
  font-size: 10px;
  font-weight: 500;
  color: ${techColors.textPrimary};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex: 1;
  min-width: 0;
  ${terminalTextStyle}
`;

export const FileSize = styled.div`
  font-size: 9px;
  color: ${techColors.textSecondary};
  flex-shrink: 0;
  ${terminalTextStyle}
`;

export const EmptyFilesMessage = styled.p`
  text-align: center;
  color: ${techColors.textSecondary};
  font-size: 12px;
  padding: 24px 16px;
  margin: 0;
  ${terminalTextStyle}
`;

export const FileUploadInput = styled.input`
  display: none;
`;

export const FilesHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  flex: 1;
  
  h3 {
    margin: 0;
    text-align: center;
  }
`;

export const FileUploadButton = styled.label`
  ${barIconButtonStyles}
  border: 1px solid ${techColors.borderSubtle};
`;

export const PlusButton = styled.button`
  ${barIconButtonStyles}

  &:active {
    transform: scale(0.98);
  }
`;

export const FilesModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.8);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

export const FilesModalContent = styled.div`
  background-color: ${techColors.darkBgSecondary};
  ${terminalBorder(techColors.neonCyan)}
  border-radius: 4px;
  padding: 8px;
  max-width: 350px;
  width: 85%;
  max-height: 50vh;
  display: flex;
  flex-direction: column;
`;

export const FilesModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  
  h3 {
    margin: 0;
    font-size: 12px;
    font-weight: 600;
    color: ${techColors.neonCyan};
    ${terminalTextStyle}
  }
`;

export const FilesModalCloseButton = styled.button`
  background: ${techColors.darkBgTertiary};
  ${terminalBorder(techColors.neonPurple)}
  font-size: 14px;
  cursor: pointer;
  color: ${techColors.neonPurple};
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;

  &:hover {
    background-color: ${techColors.darkBg};
    color: ${techColors.neonPurple};
    ${neonGlow(techColors.neonPurple, 'strong')}
  }
`;

export const FilesModalList = styled.div`
  overflow-y: auto;
  flex: 1;
  margin-bottom: 8px;
`;

export const FilesModalFooter = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 8px;
  padding-top: 8px;
  border-top: 1px solid ${techColors.borderSubtle};
`;

export const FilesModalSecondaryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: ${techColors.darkBgTertiary};
  color: ${techColors.textPrimary};
  border: 1px solid ${techColors.borderSubtle};
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  ${terminalTextStyle}

  svg {
    width: 16px;
    height: 16px;
    flex-shrink: 0;
  }

  &:hover {
    background: ${techColors.darkBg};
  }
`;

export const FilesModalButton = styled.button`
  background-color: ${techColors.darkBgSecondary};
  color: ${techColors.textPrimary};
  border: 1px solid rgba(255, 255, 255, 0.28);
  padding: 6px 16px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  ${terminalTextStyle}
  ${positiveButtonDefaultGlow}

  &:hover:not(:disabled) {
    background: ${techColors.darkBgTertiary};
    ${positiveButtonHoverStyles}
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:active:not(:disabled) {
    transform: translateY(0);
  }
`;

export const FilesModalItem = styled.div`
  padding: 6px 8px;
  margin-bottom: 3px;
  border-radius: 4px;
  ${terminalBorder(techColors.neonCyan)}
  background-color: ${techColors.darkBg};
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  color: ${techColors.textPrimary};
  min-height: 32px;

  &:hover {
    background-color: ${techColors.darkBgTertiary};
    ${neonGlow(techColors.neonCyan, 'subtle')}
  }
`;

export const SidebarWrapper = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'width' && prop !== 'isResizing',
})`
  display: flex;
  flex-direction: column;
  width: ${({ isOpen, width }) => (isOpen ? `${width}px` : '0')};
  min-width: ${({ isOpen }) => (isOpen ? '250px' : '0')};
  height: 100%;
  flex-shrink: 0;
  overflow: hidden;
  transition: ${({ isResizing }) => (isResizing ? 'none' : 'width 0.3s ease, min-width 0.3s ease')};
  ${panelSurface}
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 4px;
  box-shadow: none;

  @media (max-width: 768px) {
    position: absolute;
    inset: 0;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100%;
    height: 100%;
    z-index: 120;
    border-radius: 0;
    border: none;
    border-right: 1px solid ${techColors.borderSubtle};
    display: ${({ isOpen }) => (isOpen ? 'flex' : 'none')};
    transition: none;
    box-shadow: none;
  }
`;

export const Resizer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== 'isResizing',
})`
  width: 4px;
  height: 100%;
  background-color: transparent;
  cursor: col-resize;
  position: relative;
  flex-shrink: 0;
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  z-index: 10;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: rgba(255, 255, 255, 0.12);
  }

  &::before {
    content: '';
    position: absolute;
    left: -2px;
    top: 0;
    width: 8px;
    height: 100%;
    cursor: col-resize;
  }

  ${({ isResizing }) => isResizing && `
    background-color: rgba(255, 255, 255, 0.2);
  `}

  @media (max-width: 768px) {
    display: none;
  }
`;

export const SidebarToggleButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen' && prop !== '$inTopBar',
})`
  position: ${({ $inTopBar }) => ($inTopBar ? 'relative' : 'absolute')};
  top: ${({ $inTopBar }) => ($inTopBar ? 'auto' : '18px')};
  z-index: 101;
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${({ $inTopBar }) => ($inTopBar ? '40px' : '24px')};
  height: ${({ $inTopBar }) => ($inTopBar ? '40px' : '24px')};
  padding: 0;
  border: ${({ $inTopBar }) => ($inTopBar ? `1px solid ${techColors.borderSubtle}` : 'none')};
  background: ${({ $inTopBar }) => ($inTopBar ? techColors.darkBgTertiary : 'transparent')};
  color: ${techColors.neonPurple};
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 4px;
  flex-shrink: 0;
  ${terminalTextStyle}

  &:hover {
    color: ${techColors.neonPurple};
    background: ${({ $inTopBar }) => ($inTopBar ? techColors.darkBg : alpha(techColors.darkBgTertiary, '40'))};
    ${({ $inTopBar }) => $inTopBar && neonGlow(techColors.neonCyan, 'normal')}
  }

  &:active {
    transform: scale(0.98);
  }

  svg {
    width: 18px;
    height: 18px;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const CallHeaderButton = styled.button`
  ${barIconButtonStyles}
  color: ${techColors.neonCyan};

  &:disabled {
    opacity: 0.45;
    cursor: not-allowed;
  }

  @media (max-width: 768px) {
    width: 44px;
    height: 44px;

    svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const SectionHeader = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$static' && prop !== '$embedded' && prop !== '$footer',
})`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-shrink: 0;
  min-height: ${({ $embedded }) => ($embedded ? 'auto' : '52px')};
  padding: ${({ $embedded }) => ($embedded ? '0' : '6px 10px')};
  border-top: ${({ $footer, $embedded }) => ($footer && !$embedded ? `1px solid ${techColors.borderSubtle}` : 'none')};
  border-bottom: ${({ $embedded, $footer }) => ($embedded || $footer ? 'none' : `1px solid ${techColors.borderSubtle}`)};
  background: ${({ $embedded }) => ($embedded ? 'transparent' : techColors.darkBgSecondary)};
  box-sizing: border-box;
  cursor: ${({ $static, $embedded }) => ($static || $embedded ? 'default' : 'pointer')};
  user-select: ${({ $static, $embedded }) => ($static || $embedded ? 'auto' : 'none')};
  margin: 0;
  flex: ${({ $embedded }) => ($embedded ? '1' : 'none')};
  min-width: ${({ $embedded }) => ($embedded ? '0' : 'auto')};
  z-index: 10;

  h3 {
    margin: 0;
    font-size: 13px;
    font-weight: 600;
    color: ${techColors.textPrimary};
    ${terminalHeaderStyle}
    text-transform: none;
    letter-spacing: 0.02em;
  }

  @media (max-width: 768px) {
    min-height: ${({ $embedded }) => ($embedded ? 'auto' : '56px')};
    padding: ${({ $embedded }) => ($embedded ? '0' : '8px 10px')};
    padding-right: ${({ $embedded }) => ($embedded ? '0' : '64px')};
  }
`;

export const SectionHeaderSideSpacer = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== '$size',
})`
  width: ${({ $size }) => $size}px;
  height: 28px;
  flex-shrink: 0;
  visibility: hidden;
  pointer-events: none;
`;

export const SidebarContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  padding: 10px;
  ${messageSurface}
  position: relative;
  z-index: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${techColors.borderSubtle};
    border-radius: 3px;

    &:hover {
      background: rgba(255, 255, 255, 0.2);
    }
  }
`;

export const CollapseIcon = styled.div`
  ${barIconButtonStyles}
`;

export const AddChatButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$blink',
})`
  ${barIconButtonStyles}
  border: 1px solid ${techColors.borderSubtle};
  padding: 0;
  font-size: inherit;

  ${({ $blink }) => $blink && `
    border-color: rgba(255, 255, 255, 0.55);
    animation: menuInviteBlink 0.85s ease-in-out infinite;

    @keyframes menuInviteBlink {
      0%, 100% {
        background-color: transparent;
        border-color: rgba(255, 255, 255, 0.3);
        box-shadow: inset 0 0 0 0 rgba(255, 255, 255, 0);
        color: ${techColors.textPrimary};
      }
      50% {
        background-color: rgba(255, 255, 255, 0.14);
        border-color: rgba(255, 255, 255, 0.75);
        box-shadow: inset 0 0 12px rgba(255, 255, 255, 0.12), 0 0 10px rgba(255, 255, 255, 0.1);
        color: #fff;
      }
    }
  `}
`;

export const ChatMenuDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${techColors.darkBgSecondary};
  border: 1px solid ${techColors.borderSubtle};
  border-radius: 4px;
  width: fit-content;
  min-width: max-content;
  max-width: none;
  z-index: 10000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transition: all 0.2s ease;
  overflow: visible;
  display: flex;
  flex-direction: column;
  ${terminalTextStyle}
`;

export const ChatMenuItem = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$danger',
})`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: ${({ $danger }) => ($danger ? techColors.danger : techColors.textPrimary)};
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  ${terminalTextStyle}

  &:hover {
    background: ${techColors.darkBgTertiary};
    color: ${({ $danger }) => ($danger ? techColors.danger : techColors.neonPurple)};
    ${({ $danger }) => $danger && dangerButtonHoverStyles}
    ${({ $danger }) => !$danger && neonGlow(techColors.neonPurple, 'normal')}
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

export const MessageMenuDropdown = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isOpen',
})`
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  background: ${techColors.darkBgSecondary};
  ${terminalBorder(techColors.neonCyan)}
  border-radius: 4px;
  box-shadow: 0 0 7px ${alpha(techColors.neonCyan, '10')}, 0 0 13px ${alpha(techColors.neonCyan, '05')};
  width: fit-content;
  min-width: max-content;
  max-width: none;
  z-index: 10000;
  opacity: ${({ isOpen }) => (isOpen ? 1 : 0)};
  transform: ${({ isOpen }) => (isOpen ? 'translateY(0)' : 'translateY(-10px)')};
  pointer-events: ${({ isOpen }) => (isOpen ? 'auto' : 'none')};
  transition: all 0.2s ease;
  overflow: visible;
  display: flex;
  flex-direction: column;
  ${terminalTextStyle}
`;

export const MessageMenuItem = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$danger',
})`
  width: 100%;
  padding: 8px 12px;
  border: none;
  background: transparent;
  color: ${({ $danger }) => ($danger ? techColors.danger : techColors.textPrimary)};
  font-size: 12px;
  text-align: left;
  cursor: pointer;
  transition: all 0.3s ease;
  white-space: nowrap;
  flex-shrink: 0;
  ${terminalTextStyle}

  &:hover {
    background: ${techColors.darkBgTertiary};
    color: ${({ $danger }) => ($danger ? techColors.danger : techColors.neonPurple)};
    ${({ $danger }) => $danger && dangerButtonHoverStyles}
    ${({ $danger }) => !$danger && neonGlow(techColors.neonPurple, 'normal')}
  }

  &:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  &:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
`;

export const ChatRequestsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  overflow-y: auto;
  flex: 1;
  padding-right: 8px;
  margin-right: -8px;
  max-height: calc(7 * (32px + 8px)); /* 7 items * (height + gap) */
  min-height: calc(5 * (32px + 8px)); /* 5 items * (height + gap) */

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${techColors.darkBg};
    border: 1px solid ${alpha(techColors.neonCyan, '30')};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${alpha(techColors.neonCyan, '60')};
    border: 1px solid ${techColors.neonCyan};
    border-radius: 3px;
    box-shadow: 0 0 5px ${alpha(techColors.neonCyan, '40')};

    &:hover {
      background: ${alpha(techColors.neonCyan, '80')};
    }
  }
`;

export const ChatRequestItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  background: ${techColors.darkBgSecondary};
  border-radius: 4px;
  ${terminalBorder(techColors.neonCyan)}
  min-height: 32px;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: ${techColors.darkBgTertiary};
    ${neonGlow(techColors.neonCyan, 'normal')}
  }
`;

export const ChatRequestItemInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
`;

export const ChatRequestItemName = styled.div`
  font-weight: 500;
  color: ${techColors.textPrimary};
  font-size: 12px;
  ${terminalTextStyle}
`;

export const ChatRequestItemConnectId = styled.div`
  font-size: 10px;
  color: ${techColors.textSecondary};
  ${terminalTextStyle}
`;

export const ChatRequestItemActions = styled.div`
  display: flex;
  gap: 8px;
`;

export const ChatRequestActionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'variant',
})`
  padding: 6px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  border: none;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  ${({ variant }) => variant === 'accept' ? `
    background: ${techColors.darkBgSecondary};
    color: ${techColors.textPrimary};
    border: 1px solid rgba(255, 255, 255, 0.28);
    ${positiveButtonDefaultGlow}
    transition: all 0.3s ease;
    &:hover {
      background: ${techColors.darkBgTertiary};
      ${positiveButtonHoverStyles}
    }
  ` : variant === 'reject' ? `
    background: ${techColors.darkBgSecondary};
    color: ${techColors.danger};
    border: 1px solid ${techColors.danger};
    ${dangerButtonDefaultGlow}
    transition: all 0.3s ease;
    &:hover {
      background: ${techColors.darkBgTertiary};
      ${dangerButtonHoverStyles}
    }
  ` : `
    background: ${techColors.darkBgSecondary};
    color: ${techColors.textPrimary};
    ${terminalBorder(techColors.neonPurple)}
    &:hover {
      background: ${techColors.darkBgTertiary};
      color: ${techColors.neonPurple};
      ${neonGlow(techColors.neonPurple, 'normal')}
    }
  `}
`;

export const EmptyRequestsMessage = styled.div`
  text-align: center;
  color: ${techColors.textSecondary};
  padding: 24px 12px;
  font-size: 12px;
  line-height: 1.5;
  ${terminalTextStyle}
`;

export const ChatRequestModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  animation: fadeIn 0.2s ease-in;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

export const ChatRequestModalContent = styled.div`
  background: ${techColors.darkBgSecondary};
  border: 1px solid ${alpha(techColors.neonPurple, '40')};
  box-shadow: 
    0 0 2px ${alpha(techColors.neonPurple, '15')},
    0 0 4px ${alpha(techColors.neonPurple, '10')},
    inset 0 0 2px ${alpha(techColors.neonPurple, '07')};
  border-radius: 4px;
  padding: 12px;
  width: 90%;
  max-width: 380px;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: 8px;
  animation: slideUp 0.3s ease-out;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

export const ChatRequestsListModalOverlay = styled(ChatRequestModalOverlay)``;

export const ChatRequestsListModalContent = styled(ChatRequestModalContent)`
  max-width: 450px;
  max-height: min(85vh, 560px);
  overflow: hidden;
  min-height: 0;
`;

export const ModalScrollBody = styled.div`
  overflow-y: auto;
  flex: 1;
  min-height: 0;
  padding-right: 4px;
  margin-right: -4px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${techColors.darkBg};
    border: 1px solid ${alpha(techColors.neonCyan, '30')};
    border-radius: 3px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${alpha(techColors.neonCyan, '60')};
    border: 1px solid ${techColors.neonCyan};
    border-radius: 3px;
  }
`;

export const CompactChatRequestsList = styled(ChatRequestsList)`
  min-height: 0;
  max-height: none;
  flex: 0 0 auto;
  overflow: visible;
  padding-right: 0;
  margin-right: 0;
`;

export const ChatRequestModalHeader = styled.div`
  display: grid;
  grid-template-columns: 24px 1fr 24px;
  align-items: center;
  margin-bottom: 10px;
  gap: 8px;
`;

export const ChatRequestTabs = styled.div`
  display: flex;
  gap: 6px;
  margin-bottom: 12px;
`;

export const ChatRequestTab = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== '$active',
})`
  flex: 1;
  padding: 7px 10px;
  border: 1px solid ${({ $active }) => ($active ? 'rgba(255, 255, 255, 0.28)' : techColors.borderSubtle)};
  background: ${({ $active }) => ($active ? techColors.darkBgTertiary : techColors.darkBgSecondary)};
  color: ${({ $active }) => ($active ? techColors.textPrimary : techColors.textSecondary)};
  font-size: 11px;
  cursor: pointer;
  border-radius: 4px;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    color: ${techColors.textPrimary};
    background: ${techColors.darkBgTertiary};
    border-color: rgba(255, 255, 255, 0.2);
  }
`;

export const ChatRequestModalTitle = styled.h2`
  margin: 0;
  font-size: 14px;
  color: ${techColors.neonPurple};
  ${terminalHeaderStyle}
  text-align: center;
`;

export const ChatRequestModalCloseButton = styled.button`
  background: ${techColors.darkBgTertiary};
  border: 1px solid ${alpha(techColors.neonPurple, '40')};
  box-shadow:
    0 0 2px ${alpha(techColors.neonPurple, '15')},
    0 0 4px ${alpha(techColors.neonPurple, '10')},
    inset 0 0 2px ${alpha(techColors.neonPurple, '07')};
  font-size: 18px;
  color: ${techColors.neonPurple};
  cursor: pointer;
  padding: 0;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: all 0.2s ease;
  grid-column: 3;
  justify-self: end;

  &:hover {
    background: ${techColors.darkBg};
    color: ${techColors.neonPurple};
    box-shadow: 0 0 3px ${alpha(techColors.neonPurple, '50')}, 0 0 6px ${alpha(techColors.neonPurple, '30')};
  }
`;

export const ChatRequestModalInput = styled.input.withConfig({
  shouldForwardProp: (prop) => prop !== 'hasError',
})`
  width: 100%;
  padding: 6px 10px;
  ${({ hasError }) => terminalBorder(hasError ? techColors.danger : techColors.neonCyan)}
  border-radius: 4px;
  font-size: 12px;
  color: ${techColors.textPrimary};
  background: ${techColors.darkBg};
  margin-bottom: ${({ hasError }) => (hasError ? '5px' : '10px')};
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:focus {
    outline: none;
    border-color: ${({ hasError }) => (hasError ? techColors.danger : techColors.neonCyan)};
    ${({ hasError }) => hasError 
      ? `box-shadow: 0 0 3px ${alpha(techColors.danger, '50')}, 0 0 6px ${alpha(techColors.danger, '30')};`
      : `box-shadow: 0 0 3px ${alpha(techColors.neonCyan, '50')}, 0 0 6px ${alpha(techColors.neonCyan, '30')};`}
  }

  &::placeholder {
    color: ${techColors.textSecondary};
    opacity: 0.6;
  }
`;

export const ChatRequestModalError = styled.div`
  color: ${techColors.danger};
  font-size: 10px;
  margin-bottom: 10px;
  padding: 0 4px;
  ${terminalTextStyle}
`;

export const ChatRequestModalButtons = styled.div`
  display: flex;
  flex-direction: row;
  gap: 6px;
  justify-content: center;
  width: 100%;
`;

export const ChatRequestModalButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'primary' && prop !== '$danger',
})`
  padding: 5px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  ${({ $danger }) => $danger && `
    background: ${techColors.darkBgSecondary};
    border: 1px solid ${techColors.danger};
    color: ${techColors.danger};
    ${dangerButtonDefaultGlow}
    transition: all 0.3s ease;
    &:hover {
      background: ${techColors.darkBgTertiary};
      ${dangerButtonHoverStyles}
    }
  `}

  ${({ primary, $danger }) => primary && !$danger ? `
    background: ${techColors.darkBgSecondary};
    border: 1px solid rgba(255, 255, 255, 0.28);
    color: ${techColors.textPrimary};
    ${positiveButtonDefaultGlow}
    transition: all 0.3s ease;
    &:hover {
      background: ${techColors.darkBgTertiary};
      ${positiveButtonHoverStyles}
    }
  ` : ''}

  ${({ primary, $danger }) => !primary && !$danger ? `
    background: ${techColors.darkBgTertiary};
    border: 1px solid ${techColors.borderSubtle};
    color: ${techColors.textSecondary};
    &:hover {
      background: ${techColors.darkBg};
      color: ${techColors.textPrimary};
    }
  ` : ''}

  &:disabled {
    opacity: 0.5;
  }
`;

// Trust User Modal Styles
export const TrustPeerModalDescription = styled.p`
  margin: 0 0 8px 0;
  color: ${techColors.textPrimary};
  font-size: 12px;
  ${terminalTextStyle}

  strong {
    color: ${techColors.neonPurple};
  }
`;

export const TrustPeerOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const TrustPeerOptionItem = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isSelected',
})`
  padding: 6px 10px;
  border-radius: 4px;
  background: ${({ isSelected }) => (isSelected ? `${techColors.darkBgTertiary}` : `${techColors.darkBgSecondary}`)};
  ${({ isSelected }) => terminalBorder(isSelected ? techColors.neonPurple : techColors.textSecondary)}
  cursor: pointer;
  transition: all 0.2s ease;
  ${terminalTextStyle}

  &:hover {
    background: ${techColors.darkBgTertiary};
    ${terminalBorder(techColors.neonPurple)}
    ${neonGlow(techColors.neonPurple, 'normal')}
  }
`;

export const TrustPeerOptionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  margin-bottom: ${({ hasDescription }) => (hasDescription ? '2px' : '0')};
`;

export const TrustPeerCheckbox = styled.input.attrs({ type: 'checkbox' })`
  width: 14px;
  height: 14px;
  cursor: pointer;
  accent-color: ${techColors.neonPurple};
  filter: brightness(1.2);
  
  &:checked {
    filter: brightness(1.5) drop-shadow(0 0 3px ${techColors.neonPurple});
  }
`;

export const TrustPeerOptionLabel = styled.div`
  font-weight: 600;
  color: ${techColors.textPrimary};
  font-size: 12px;
  ${terminalTextStyle}
`;

export const TrustPeerOptionDescription = styled.div`
  font-size: 10px;
  color: ${techColors.textSecondary};
  margin-left: 19px;
  ${terminalTextStyle}
`;

// Delete Chat Modal Styles
export const DeleteChatModalDescription = styled.p`
  margin: 0 0 8px 0;
  color: ${techColors.textPrimary};
  font-size: 12px;
  ${terminalTextStyle}

  strong {
    color: ${techColors.neonPurple};
  }
`;

export const DeleteChatOptionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const DeleteChatOptionButton = styled.button.withConfig({
  shouldForwardProp: (prop) => prop !== 'isDanger',
})`
  padding: 6px 10px;
  border-radius: 4px;
  background: ${techColors.darkBgSecondary};
  border: 1px solid ${({ isDanger }) => (isDanger ? techColors.danger : techColors.borderSubtle)};
  color: ${({ isDanger }) => (isDanger ? techColors.danger : techColors.textPrimary)};
  cursor: pointer;
  text-align: left;
  transition: all 0.3s ease;
  ${terminalTextStyle}
  ${({ isDanger }) => isDanger && dangerButtonDefaultGlow}

  &:hover {
    background: ${techColors.darkBgTertiary};
    ${({ isDanger }) => isDanger && dangerButtonHoverStyles}
  }
`;

export const DeleteChatOptionTitle = styled.div`
  font-weight: 600;
  margin-bottom: 2px;
  font-size: 12px;
  ${terminalTextStyle}
`;

export const DeleteChatOptionDescription = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== 'isDanger',
})`
  font-size: 10px;
  color: ${({ isDanger }) => (isDanger ? alpha(techColors.danger, 'cc') : techColors.textSecondary)};
  ${terminalTextStyle}
`;

export const ChatsLoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 3px solid ${alpha(techColors.neonCyan, '30')};
  border-top: 3px solid ${techColors.neonCyan};
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 40px auto;
  box-shadow: 0 0 15px ${alpha(techColors.neonCyan, '40')};

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;
